import findResult, {
  assignIfExists,
  removeItemArr,
  previousCarouselKey,
  hasKey,
} from '@/utils/resultsHelper';
import { findWithUUID, removeItemByUUID } from '@/utils/uuidHelper';
import { states } from '@/constants/lifecycle';
import withDraft from '@/utils/draftHelper';
import { alignFieldsToForm } from '@/forms/state/state';
import { cascadeFieldTypeUpdate } from '../../fields/helper';
import { datasetLifeCycle, datasetSteps } from '../../constants';
import actions from '../constants';
import findDataset from '../../state/shared';
import { assignEditProps, assignEditPropsNoSplit, markDatasetAsDirty } from '../../state/state';
import newDictionary, {
  activeDictionaries,
  activeDictionaryEdit,
  firstDictionary,
  splitUrlsInDictionaries,
  pruneActiveDictionaryEditErrors,
  updateLookupType,
  assignEditPropsToActiveLookups,
  removeLookupItem,
  updateLookupName,
  findDatasetDictionary,
  findDatasetDictionaryField,
  findActiveDatasetDictionary,
  applyPrimaryDictionary,
} from './state';
import newDictionaryField, { addNewField, assignLookupToField, activeField } from '../../fields/state';
import newLookupItem, { activeLookup, lookupProps } from '../../lookups/state';
import { validationWarningDictionariesLookups } from '../../lookups/helper';

const updateActiveDictionary = withDraft((draft, action) => {
  const { item, dictionary } = findDatasetDictionary(draft, action);

  if (dictionary) {
    item.activeDictionaryName = dictionary.name;
    item.activeDictionaryUUID = dictionary.uuid;
  }

  assignIfExists(draft.lookups, lookupProps(draft, action));
});

const deleteActiveDictionary = withDraft((draft, action) => {
  const { item, dictionary } = findDatasetDictionary(draft, action);

  assignEditProps(item, action.template);
  markDatasetAsDirty(item, datasetSteps.DICTIONARIES, 'dictionaries');

  item.dictionaries = removeItemArr(item.dictionaries, dictionary);
  const activeDictionaryForDeletion = firstDictionary(item.dictionaries);
  item.activeDictionaryName = activeDictionaryForDeletion.name;
  item.activeDictionaryUUID = activeDictionaryForDeletion.uuid;
});

const showDictionaryField = withDraft((draft, action) => {
  const item = findDataset(draft, action);
  const validationWarnings = [];

  if (action.withValidation) {
    validationWarningDictionariesLookups(item.dictionaries, validationWarnings);
  }

  assignIfExists(draft.lookups, {
    ...action,
    fieldsOpen: true,
    isEditing: item.isEditing && !item.asViewer.on,
    isNew: item.isNew,
    dictionaries: item.dictionaries,
    activeDictionaryUUID: item.activeDictionaryUUID,
    permissions: item.permissions,
    validationWarnings,
  });
});

const showDictionaryLookups = withDraft((draft, action) => {
  const { item, dictionary } = findActiveDatasetDictionary(draft, action);
  const dictionaryLookups = dictionary ? dictionary.lookups || {} : {};
  const validationWarnings = [];

  if (action.withValidation) {
    validationWarningDictionariesLookups(item.dictionaries, validationWarnings);
  }

  assignIfExists(draft.lookups, {
    ...action,
    lookupsOpen: true,
    isEditing: item.isEditing && !item.asViewer.on,
    isNew: item.isNew,
    dictionaries: item.dictionaries,
    activeDictionaryUUID: item.activeDictionaryUUID,
    activeLookupName: action.activeLookup || Object.keys(dictionaryLookups)[0],
    permissions: item.permissions,
    validationWarnings,
  });
});

const addDictionaryField = withDraft((draft, action) => {
  const { item, dictionary } = findActiveDatasetDictionary(draft, action);

  assignEditProps(item, action.template);
  dictionary.fields.push(newDictionaryField());
  markDatasetAsDirty(item, datasetSteps.DICTIONARIES, 'dictionaries');
});

const deleteDictionaryField = withDraft((draft, action) => {
  const { item, dictionary } = findDatasetDictionary(draft, action);

  assignEditProps(item, action.template);
  dictionary.fields = removeItemByUUID(dictionary.fields, action.fieldUUID);
  markDatasetAsDirty(item, datasetSteps.DICTIONARIES, 'dictionaries');
});

const copyDictionaryField = withDraft((draft, action) => {
  const { item, dictionary, field } = findDatasetDictionaryField(draft, action);
  assignEditProps(item, action.template);

  if (field) {
    const newField = newDictionaryField();
    newField.label = field.label;
    newField.description = field.description;
    newField.type = field.type;
    newField.constraints = field.constraints;
    newField.uri = field.uri;
    newField.entity = field.entity;
    newField.cohort_filter = field.cohort_filter;
    if (hasKey(field, 'pseudonymisation_rule')) {
      newField.pseudonymisation_rule = field.pseudonymisation_rule;
    }
    dictionary.fields.push(newField);
    markDatasetAsDirty(item, datasetSteps.DICTIONARIES, 'dictionaries');
  }
});

const updateDictionaryFields = withDraft((draft, action) => {
  const { item, dictionary, field } = findDatasetDictionaryField(draft, action);

  assignEditProps(item, action.template);
  if (field) {
    assignIfExists(field, action.properties);
  } else {
    addNewField(dictionary.fields, action.fieldUUID, action.properties);
  }
  markDatasetAsDirty(item, datasetSteps.DICTIONARIES, 'dictionaries');
});

const updateDictionaryFieldType = withDraft((draft, action) => {
  const { item, field } = findDatasetDictionaryField(draft, action);

  assignEditProps(item, action.template);
  assignIfExists(field, { type: action.fieldType, constraints: action.fieldConstraint });
  cascadeFieldTypeUpdate(field, action.pmnFunctions);
  markDatasetAsDirty(item, datasetSteps.DICTIONARIES, 'dictionaries');
});

const showEditDictionary = withDraft((draft, action) => {
  const { item, dictionary } = findDatasetDictionary(draft, action, {
    code: null,
    name: '',
    source_name: '',
    description: '',
    primary: false,
  });
  if (action.isNew && !item.dictionaries.some(({ primary }) => primary)) {
    dictionary.primary = true;
  }

  assignIfExists(
    draft.activeDictionaryEdit,
    {
      ...action,
      open: true,
      dictionaryCode: dictionary.code,
      primary: dictionary.primary,
      isPersisted: Boolean(dictionary.id),
      originalName: dictionary.name,
      dictionaryName: dictionary.name,
      dictionarySourceName: dictionary.source_name,
      dictionaryDescription: dictionary.description,
    },
    [],
  );
});

const updateDictionaryProperties = withDraft((draft, action) => {
  const { item, dictionary } = findDatasetDictionary(draft, action);

  assignEditProps(item, action.template);
  assignIfExists(dictionary, action.properties);
  assignIfExists(item, { activeDictionaryName: action.properties.name }, datasetLifeCycle);
  markDatasetAsDirty(item, datasetSteps.DICTIONARIES, 'dictionaries');
  if (dictionary.primary) {
    applyPrimaryDictionary(item, dictionary);
  }
  draft.activeDictionaryEdit = activeDictionaryEdit();
});

const insertActiveDictionary = withDraft((draft, action) => {
  const item = findDataset(draft, action);
  assignEditProps(item, action.template);

  const newActiveDictionary = newDictionary(action.properties);
  item.dictionaries.push(newActiveDictionary);
  if (newActiveDictionary.primary) {
    applyPrimaryDictionary(item, newActiveDictionary);
  }

  const isDirty = assignIfExists(
    item,
    {
      activeDictionaryName: newActiveDictionary.name,
      activeDictionaryUUID: newActiveDictionary.uuid,
    },
    datasetLifeCycle,
  );

  if (isDirty) {
    markDatasetAsDirty(item, datasetSteps.DICTIONARIES, 'dictionaries');
  }
  draft.activeDictionaryEdit = activeDictionaryEdit();
});

const datasetDictionaryUpload = withDraft((draft, action) => {
  const item = findDataset(draft, action);
  item.isDirty.dictionaryfile = true;
  item.uploadStatus = states.PENDING;
});

const datasetDictionaryUploadSuccess = withDraft((draft, action) => {
  const item = findDataset(draft, action);
  item.isDirty.dictionaryfile = false;
  item.uploadStatus = states.SUCCESS;
});

const datasetDictionaryUploadFail = withDraft((draft, action) => {
  const item = findDataset(draft, action);
  item.isDirty.dictionaryfile = false;
  item.uploadStatus = states.ERROR;
});

const addDatasetDictionaryUploadMessage = withDraft((draft, action) => {
  const item = findDataset(draft, action);
  item.dictionaryUploadMessages = action.msg;
  item.dictionaryUploadMessagesError = !action.error;
});

const removeDatasetDictionaryFileUpload = withDraft((draft, action) => {
  const item = findDataset(draft, action);
  if (item) {
    item.isDirty.dictionaryfile = false;
    item.uploadStatus = states.NONE;
  }
});

const toggleDictionaryFieldsProperty = withDraft((draft, action) => {
  const { item, dictionary } = findDatasetDictionary(draft, action);
  assignEditProps(item, action.template);
  dictionary.fields = dictionary.fields.map((field) => ({ ...field, [action.name]: action.selected }));
  markDatasetAsDirty(item, datasetSteps.DICTIONARIES, 'dictionaries'); // TODO - check for changes?
});

const updateActiveDictionaryEdit = withDraft((draft, action) => {
  assignIfExists(draft.activeDictionaryEdit, action.properties);
  draft.activeDictionaryEdit.saveErrors = pruneActiveDictionaryEditErrors(
    draft.activeDictionaryEdit.saveErrors,
    action,
  );
});

const hideEditDictionary = withDraft((draft) => { draft.activeDictionaryEdit = activeDictionaryEdit(); });

const resetLookups = withDraft((draft) => { draft.lookups = activeDictionaries(); });

const updateActiveLookup = withDraft((draft, action) => { draft.lookups.activeLookupName = action.name; });

const updateActiveField = withDraft((draft, action) => {
  draft.lookups.activeFieldUUID = action.fieldUUID;
  const field = activeField(draft.lookups);
  draft.lookups.activeLookupName = field?.constraints || '';
});

const updateActiveLookupType = withDraft((draft, action) => { updateLookupType(draft.lookups, action); });

const addLookupItem = withDraft((draft) => {
  assignEditPropsToActiveLookups(draft.lookups);
  activeLookup(draft.lookups).options.push(newLookupItem());
});

const deleteLookupItem = withDraft((draft, action) => { removeLookupItem(draft.lookups, action); });

const showEditActiveLookup = withDraft((draft, action) => {
  draft.lookups.lookupBeingEdited.isEditing = true;
  draft.lookups.lookupBeingEdited.name = action.isNew ? '' : draft.lookups.activeLookupName;
  draft.lookups.lookupBeingEdited.isNew = action.isNew;
});

const hideEditActiveLookup = withDraft((draft, action) => {
  if (!action.cancel) {
    updateLookupName(draft.lookups, action);
  }
  draft.lookups.lookupBeingEdited = activeDictionaries().lookupBeingEdited;
});

const deleteActiveLookup = withDraft((draft) => {
  const name = draft.lookups.activeLookupName;
  const dictionary = assignLookupToField(draft.lookups);
  const previous = previousCarouselKey(dictionary.lookups, name);

  draft.lookups.activeLookupName = previous === name ? '' : previous;
  delete dictionary.lookups[name];
  assignEditPropsToActiveLookups(draft.lookups);
});

const updateLookupProperties = withDraft((draft, action) => {
  if (action.properties.isEditing) {
    splitUrlsInDictionaries(draft.lookups);
  }
  assignIfExists(draft.lookups, action.properties);
});

const updateActiveLookupProperties = withDraft((draft, action) => {
  assignIfExists(draft.lookups.lookupBeingEdited, action.properties);
});

const applyDictionaries = withDraft((draft, action) => {
  const item = findResult(draft.datasets, draft.lookups.category, draft.lookups.code);

  if (assignEditPropsNoSplit(item)) {
    const { errors } = alignFieldsToForm(item.catalogue, action.template);
    errors.forEach((e) => item.saveErrors.push(e));
  }

  item.dictionaries = draft.lookups.dictionaries;
  draft.lookups = activeDictionaries();
  markDatasetAsDirty(item, datasetSteps.DICTIONARIES, 'dictionaries');
});

const updateLookupItemProperties = withDraft((draft, action) => {
  const item = findWithUUID(activeLookup(draft.lookups).options, action.lookupItemUUID);
  assignIfExists(item, action.properties);
  assignEditPropsToActiveLookups(draft.lookups);
});

export default {
  [actions.ADD_DATASET_DICTIONARY_UPLOAD_MESSAGE]: addDatasetDictionaryUploadMessage,
  [actions.ADD_DICTIONARY_FIELD]: addDictionaryField,
  [actions.ADD_LOOKUP_ITEM]: addLookupItem,
  [actions.APPLY_DICTIONARIES]: applyDictionaries,
  [actions.COPY_DICTIONARY_FIELD]: copyDictionaryField,
  [actions.DATASET_DICTIONARY_UPLOAD]: datasetDictionaryUpload,
  [actions.DATASET_DICTIONARY_UPLOAD_FAIL]: datasetDictionaryUploadFail,
  [actions.DATASET_DICTIONARY_UPLOAD_SUCCESS]: datasetDictionaryUploadSuccess,
  [actions.DELETE_ACTIVE_DICTIONARY]: deleteActiveDictionary,
  [actions.DELETE_ACTIVE_LOOKUP]: deleteActiveLookup,
  [actions.DELETE_DICTIONARY_FIELD]: deleteDictionaryField,
  [actions.DELETE_LOOKUP_ITEM]: deleteLookupItem,
  [actions.HIDE_DICTIONARY_FIELD]: resetLookups,
  [actions.HIDE_DICTIONARY_LOOKUPS]: resetLookups,
  [actions.HIDE_EDIT_ACTIVE_LOOKUP]: hideEditActiveLookup,
  [actions.HIDE_EDIT_DICTIONARY]: hideEditDictionary,
  [actions.INSERT_ACTIVE_DICTIONARY]: insertActiveDictionary,
  [actions.REMOVE_DATASET_DICTIONARY_FILE_UPLOAD]: removeDatasetDictionaryFileUpload,
  [actions.SHOW_DICTIONARY_FIELD]: showDictionaryField,
  [actions.SHOW_DICTIONARY_LOOKUPS]: showDictionaryLookups,
  [actions.SHOW_EDIT_ACTIVE_LOOKUP]: showEditActiveLookup,
  [actions.SHOW_EDIT_DICTIONARY]: showEditDictionary,
  [actions.TOGGLE_DICTIONARY_FIELDS_PROPERTY]: toggleDictionaryFieldsProperty,
  [actions.UPDATE_ACTIVE_DICTIONARY]: updateActiveDictionary,
  [actions.UPDATE_ACTIVE_DICTIONARY_EDIT]: updateActiveDictionaryEdit,
  [actions.UPDATE_ACTIVE_FIELD]: updateActiveField,
  [actions.UPDATE_ACTIVE_LOOKUP]: updateActiveLookup,
  [actions.UPDATE_ACTIVE_LOOKUP_PROPERTIES]: updateActiveLookupProperties,
  [actions.UPDATE_ACTIVE_LOOKUP_TYPE]: updateActiveLookupType,
  [actions.UPDATE_DICTIONARY_FIELDS]: updateDictionaryFields,
  [actions.UPDATE_DICTIONARY_FIELD_TYPE]: updateDictionaryFieldType,
  [actions.UPDATE_DICTIONARY_PROPERTIES]: updateDictionaryProperties,
  [actions.UPDATE_LOOKUP_ITEM_PROPERTIES]: updateLookupItemProperties,
  [actions.UPDATE_LOOKUP_PROPERTIES]: updateLookupProperties,
};
